import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import React from "react";
import { useFragment } from "react-relay";

import {
  Radio,
  RadioGroup,
} from "@whoosh/design-system/src/components/controls";
import {
  ChargePayerRow,
  EventChargePayerRowData,
  EventChargePayerRowDataPayForOption,
} from "@whoosh/design-system/src/components/display/ChargePayerRow/ChargePayerRow";
import {
  ScrollView,
  VStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";

import { setNumberOfUnknownGuestsMemberWillPayForAtom } from "../../atoms/actions";
import {
  eventPrimaryChargePaymentDataAtom,
  eventSignUpProcessDispatchAtom,
  primaryChargeQuickPayOptionAtom,
  userTypeAtom,
} from "../../atoms/eventSignUpProcess";
import { unknownGuestsPerPrimaryChargeTypeAtom } from "../../atoms/selectors";
import { unknownGuestsAssignedToPayersAtom } from "../../atoms/stores";
import { useGetPrimaryChargeNameAndPrice } from "../../hooks/useGetPrimaryChargeNameAndPrice";
import { FormStepHeader } from "../FormStepHeader";
import { EventPrimaryChargeType } from "../__generated__/FormStepManager_event.graphql";
import { EventSignUpPrimaryChargePaymentAssignment_event$key } from "./__generated__/EventSignUpPrimaryChargePaymentAssignment_event.graphql";

export const EventSignUpPrimaryChargePaymentAssignment = ({
  primaryChargeType,
  eventRef,
}: {
  primaryChargeType: EventPrimaryChargeType | null;
  eventRef: EventSignUpPrimaryChargePaymentAssignment_event$key;
}) => {
  const data = useFragment(
    EventSignUpPrimaryChargePaymentAssignmentFragmentString,
    eventRef
  );
  const primaryChargeNamesAndPrices = useGetPrimaryChargeNameAndPrice({
    eventRef: data,
  });
  const dispatch = useSetAtom(eventSignUpProcessDispatchAtom);
  const payerData = useAtomValue(eventPrimaryChargePaymentDataAtom);
  const curQuickOptionsValue = useAtomValue(primaryChargeQuickPayOptionAtom);
  const userType = useAtomValue(userTypeAtom);
  const unknownGuestsAssignedToPayers = useAtomValue(
    unknownGuestsAssignedToPayersAtom
  );
  const unknownGuestsByChargeType = useAtomValue(
    unknownGuestsPerPrimaryChargeTypeAtom
  );

  const setNumberOfUnknownGuestsToPayFor = useSetAtom(
    setNumberOfUnknownGuestsMemberWillPayForAtom
  );

  const getUnknownGuestsForPayer = (payerId: string) => {
    return unknownGuestsByChargeType.flatMap((item) => {
      const matchingPriceAndNameEntry = primaryChargeNamesAndPrices.find(
        (n) => n.primaryChargeId === item.primaryChargeId
      );
      const matchingPlayerCountEntry = unknownGuestsAssignedToPayers.find(
        (n) =>
          n.primaryChargeId === item.primaryChargeId && n.payerId === payerId
      );
      if (matchingPriceAndNameEntry) {
        return {
          primaryChargeName: matchingPriceAndNameEntry.chargeName,
          primaryChargePriceInCents: matchingPriceAndNameEntry.chargePrice,
          primaryChargeId: item.primaryChargeId,
          countAssignedToPayer:
            matchingPlayerCountEntry?.unknownGuestCount ?? 0,
          maximum: item?.total ?? 0,
        };
      }
      return [];
    });
  };

  const onChangePayersPayedForPlayers = (
    payerId: string,
    payedForIds: string[]
  ) => {
    dispatch({
      type: "updatePrimaryChargePayForWhoChoices",
      payload: { payerId, payedForIds },
    });
  };

  const onChangeUnknownGuests = (
    payerId: string,
    primaryChargeId: string,
    unknownGuestCount: number
  ) => {
    setNumberOfUnknownGuestsToPayFor({
      payerId,
      primaryChargeId,
      unknownGuestCount,
    });
  };

  const getPayForOptions = (
    payer: EventChargePayerRowData
  ): EventChargePayerRowDataPayForOption[] => {
    const payForOptions = payerData
      .flatMap((p) => {
        /**
         * If the person has no additional folks they're paying for,
         * other than unknown guests
         */
        if (
          !p.additionalChargeRows.find((r) => !(r.type === "GUEST" && !r.id))
        ) {
          const defaultItem: EventChargePayerRowDataPayForOption = {
            value: p.payer.id,
            type: "row",
            label: p.payer.firstName + " " + p.payer.lastName,
            isDisabled: false,
          };
          return [defaultItem];
        } else {
          return p.additionalChargeRows.flatMap((row, i) => {
            const guestIsPaidForByRowPlayer =
              row?.type === "GUEST" && row.payingMemberId === payer?.payer?.id;

            const isUnknownGuest = row.type === "GUEST" && !row.id;
            const newRow: EventChargePayerRowDataPayForOption = {
              value: row.id,
              type: "row",
              label: row.firstName + " " + row.lastName,
              isDisabled: !row.id.length || guestIsPaidForByRowPlayer,
            };
            return !isUnknownGuest ? [newRow] : [];
          });
        }
      })
      .filter((option) => option.value !== payer.payer.id);

    if (unknownGuestsByChargeType.length > 0) {
      payForOptions.push({
        value: "unknown-guests",
        type: "unknownGuest",
        label: t`Unknown Guest(s)`,
        isDisabled: false,
      });
    }

    return payForOptions;
  };

  return (
    <VStack flex={1} pt={userType === "STAFF" ? 4 : 0}>
      <FormStepHeader title={t`Payment`} />
      <ScrollView>
        <VStack px={6} mb={10}>
          <Text maxFontSizeMultiplier={2} mb={6} fontSize={16}>
            {t`How do you want to split the bill?`}
          </Text>
          <RadioGroup
            value={curQuickOptionsValue}
            onChange={(nextValue) => {
              if (
                nextValue === "custom" ||
                nextValue === "everyone_pays_for_themselves" ||
                nextValue === "you_pay_for_everyone"
              ) {
                dispatch({
                  type: "quickUpdatePrimaryChargePaymentOption",
                  payload: { paymentOption: nextValue },
                });
              }
            }}
          >
            <Radio
              value="you_pay_for_everyone"
              label={
                userType === "MEMBER"
                  ? t`You pay for everyone`
                  : t`First member pays for everyone`
              }
            />
            <Radio
              value="everyone_pays_for_themselves"
              label={t`Everyone pays for self`}
            />
            <Radio value="custom" label={t`Custom`} />
          </RadioGroup>
        </VStack>

        <Text
          accessibilityRole="header"
          variant="mainTitle3"
          maxFontSizeMultiplier={2}
          px={6}
          mb={6}
        >{t`Summary`}</Text>

        {payerData.map((payer) => {
          const payForOptions = getPayForOptions(payer);
          const unknownGuests = getUnknownGuestsForPayer(payer.payer.id);

          return (
            <ChargePayerRow
              {...payer}
              key={payer.payer.id}
              shouldShowPayForDropdown={curQuickOptionsValue === "custom"}
              payForOptions={payForOptions}
              onChangePayersPayedForPlayers={onChangePayersPayedForPlayers}
              onChangeUnknownGuests={onChangeUnknownGuests}
              unknownGuestsOptionsForPayer={unknownGuests}
              primaryChargeType={primaryChargeType}
              isPaymentAssignment={true}
            />
          );
        })}
      </ScrollView>
    </VStack>
  );
};

const EventSignUpPrimaryChargePaymentAssignmentFragmentString = graphql`
  fragment EventSignUpPrimaryChargePaymentAssignment_event on Event {
    ...useGetPrimaryChargeNameAndPrice_event
    memberSignUpProxyAllowed
    prePaymentRequirement
  }
`;
